'use client'

// SUMMARY: The in-chat document-wizard form card. Renders the orchestrator's requested fields (text /
// number / date / textarea / select), validates required ones, and on submit sends the answers back into
// the chat as a readable message (via DocumentFormContext) which continues the wizard to generation.

import { useState } from 'react'
import { useLocale } from 'next-intl'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { FormField } from '@/lib/ai/document/wizard-form'
import { useDocumentFormActions } from './document-form-context'

const INPUT_CLASS =
	'w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/50'

export function DocumentForm({ title, fields }: { title: string; fields: FormField[] }) {
	const locale = useLocale()
	const de = locale === 'de'
	const actions = useDocumentFormActions()
	const [values, setValues] = useState<Record<string, string>>(() =>
		Object.fromEntries(fields.map((f) => [f.key, f.value ?? '']))
	)
	const [submitted, setSubmitted] = useState(false)

	const set = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }))
	const missingRequired = fields.some((f) => f.required && !values[f.key]?.trim())
	const disabled = submitted || actions?.disabled || missingRequired

	const submit = () => {
		if (!actions || submitted || missingRequired) return
		const lines = fields.map(
			(f) => `- ${f.label}: ${values[f.key]?.trim() || (de ? '(keine Angabe)' : '(not provided)')}`
		)
		const text =
			`${de ? 'Meine Angaben für' : 'My details for'} ${title}:\n${lines.join('\n')}\n\n` +
			(de ? 'Bitte das Dokument jetzt erstellen.' : 'Please create the document now.')
		setSubmitted(true)
		actions.onSubmit(text)
	}

	return (
		<div className="my-3 overflow-hidden rounded-xl border border-border">
			<div className="flex items-center gap-2 border-b border-border bg-foreground/[0.03] px-3.5 py-2.5">
				<FileText className="h-4 w-4 shrink-0 text-primary" />
				<span className="text-sm font-semibold">{title}</span>
			</div>
			<div className="space-y-3 p-3.5">
				{fields.map((field) => (
					<label key={field.key} className="block space-y-1">
						<span className="text-xs font-medium text-muted-foreground">
							{field.label}
							{field.required && <span className="ml-0.5 text-destructive">*</span>}
						</span>
						<FieldInput
							field={field}
							value={values[field.key] ?? ''}
							disabled={submitted}
							onChange={(v) => set(field.key, v)}
						/>
					</label>
				))}
				<Button onClick={submit} disabled={disabled} className="w-full">
					{submitted ? (de ? 'Übermittelt' : 'Submitted') : de ? 'Dokument erstellen' : 'Create document'}
				</Button>
			</div>
		</div>
	)
}

function FieldInput({
	field,
	value,
	disabled,
	onChange,
}: {
	field: FormField
	value: string
	disabled: boolean
	onChange: (v: string) => void
}) {
	if (field.type === 'textarea') {
		return (
			<textarea
				className={`${INPUT_CLASS} min-h-[64px] resize-y`}
				placeholder={field.placeholder}
				value={value}
				disabled={disabled}
				onChange={(e) => onChange(e.target.value)}
			/>
		)
	}
	if (field.type === 'select') {
		return (
			<select className={INPUT_CLASS} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
				<option value="">—</option>
				{(field.options ?? []).map((opt) => (
					<option key={opt} value={opt}>
						{opt}
					</option>
				))}
			</select>
		)
	}
	return (
		<input
			type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
			className={INPUT_CLASS}
			placeholder={field.placeholder}
			value={value}
			disabled={disabled}
			onChange={(e) => onChange(e.target.value)}
		/>
	)
}
